import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';

function formatMinutes(minutes = 0) {
  const safeMinutes = Math.max(0, Number(minutes) || 0);
  const hours = Math.floor(safeMinutes / 60);
  const remainder = safeMinutes % 60;
  if (!hours) return `${remainder}m`;
  if (!remainder) return `${hours}h`;
  return `${hours}h ${String(remainder).padStart(2, '0')}m`;
}

function formatDateLabel(value) {
  if (!value) return 'Sem prazo definido';
  const parsed = new Date(`${String(value).slice(0, 10)}T12:00:00`);
  return Number.isNaN(parsed.getTime()) ? 'Sem prazo definido' : parsed.toLocaleDateString('pt-BR');
}

function ProgressBar({ value, color = 'bg-blue-500', height = 'h-2.5' }) {
  return (
    <div className={`w-full ${height} overflow-hidden rounded-full bg-slate-200`}>
      <div className={`h-full ${color} rounded-full transition-all`} style={{ width: `${Math.max(0, Math.min(100, value || 0))}%` }} />
    </div>
  );
}

function getCompletionColor(pct) {
  if (pct >= 80) return 'bg-emerald-500';
  if (pct >= 55) return 'bg-amber-500';
  return 'bg-rose-500';
}

function getRiskTone(score) {
  if (score >= 12) return 'border-rose-200 bg-rose-50 text-rose-700';
  if (score >= 6) return 'border-amber-200 bg-amber-50 text-amber-700';
  return 'border-emerald-200 bg-emerald-50 text-emerald-700';
}

function MetricCard({ label, value, detail, tone }) {
  return (
    <Card className={`overflow-hidden border-0 shadow-sm ${tone}`}>
      <CardContent className="p-5">
        <p className="text-xs font-semibold uppercase tracking-[0.16em] opacity-75">{label}</p>
        <p className="mt-4 text-3xl font-bold tracking-tight">{value}</p>
        <p className="mt-2 text-sm opacity-80">{detail}</p>
      </CardContent>
    </Card>
  );
}

function DistributionRow({ label, count, percentage, color }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-3 text-sm">
        <span className="font-medium text-slate-700">{label}</span>
        <span className="text-slate-500">{count} · {percentage}%</span>
      </div>
      <ProgressBar value={percentage} color={color} height="h-2" />
    </div>
  );
}

export default function PortfolioView({ api, boards }) {
  const [portfolios, setPortfolios] = useState([]);
  const [selectedPortfolio, setSelectedPortfolio] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', board_ids: [], color: '#0073ea' });

  useEffect(() => { fetchPortfolios(); }, []);

  const fetchPortfolios = async () => {
    try {
      const res = await api.get('/projects/portfolios');
      const nextPortfolios = res.data || [];
      setPortfolios(nextPortfolios);
      if (selectedPortfolio) {
        const refreshedSelected = nextPortfolios.find(portfolio => portfolio.id === selectedPortfolio.id);
        if (refreshedSelected) setSelectedPortfolio(refreshedSelected);
      }
    } catch (error) {
      console.error(error);
      toast.error('Erro ao carregar portfolios');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async (portfolioId) => {
    setStatsLoading(true);
    try {
      const res = await api.get(`/projects/portfolios/${portfolioId}/stats`);
      setStats(res.data);
    } catch (error) {
      console.error(error);
      toast.error('Erro ao carregar dashboard do portfolio');
    } finally {
      setStatsLoading(false);
    }
  };

  const selectPortfolio = (portfolio) => {
    setSelectedPortfolio(portfolio);
    fetchStats(portfolio.id);
  };

  const handleCreate = async () => {
    if (!form.name) {
      toast.error('Nome e obrigatorio');
      return;
    }
    try {
      await api.post('/projects/portfolios', form);
      toast.success('Portfolio criado!');
      setShowCreateModal(false);
      setForm({ name: '', description: '', board_ids: [], color: '#0073ea' });
      fetchPortfolios();
    } catch (error) {
      toast.error('Erro ao criar portfolio');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Excluir este portfolio?')) return;
    try {
      await api.delete(`/projects/portfolios/${id}`);
      setPortfolios(current => current.filter(portfolio => portfolio.id !== id));
      if (selectedPortfolio?.id === id) {
        setSelectedPortfolio(null);
        setStats(null);
      }
      toast.success('Portfolio excluido');
    } catch (error) {
      toast.error('Erro ao excluir');
    }
  };

  const toggleBoardInForm = (boardId) => {
    setForm(current => ({
      ...current,
      board_ids: current.board_ids.includes(boardId)
        ? current.board_ids.filter(id => id !== boardId)
        : [...current.board_ids, boardId]
    }));
  };

  const boardLookup = useMemo(
    () => new Map((boards || []).map(board => [board.id, board])),
    [boards]
  );
  const selectedBoards = useMemo(
    () => (selectedPortfolio?.board_ids || []).map(id => boardLookup.get(id)).filter(Boolean),
    [boardLookup, selectedPortfolio]
  );

  if (loading) {
    return <div className="flex h-64 items-center justify-center"><div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600"></div></div>;
  }

  const summary = stats?.summary || null;
  const statusDistribution = stats?.status_distribution || [];
  const priorityDistribution = stats?.priority_distribution || [];
  const dueBuckets = stats?.due_buckets || {};
  const riskBoards = stats?.risk_boards || [];
  const criticalItems = stats?.critical_items || [];
  const assigneeWorkload = stats?.assignee_workload || [];
  const boardCards = stats?.boards || [];
  const recentActivity = stats?.recent_activity || {};

  return (
    <div className="flex flex-col lg:flex-row min-h-[720px] lg:h-full bg-[linear-gradient(180deg,_#f8fafc,_#eef4ff_42%,_#f8fafc)]">
      <aside className="w-full lg:w-72 shrink-0 border-b lg:border-b-0 lg:border-r border-slate-200 bg-white/90 p-4 backdrop-blur">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-blue-600">Portfolio Hub</p>
            <h3 className="mt-1 font-semibold text-slate-900">Portfolios</h3>
          </div>
          <Button size="sm" variant="ghost" onClick={() => setShowCreateModal(true)}>+</Button>
        </div>

        <div className="space-y-2">
          {portfolios.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-center text-sm text-slate-500">
              Nenhum portfolio ainda
            </div>
          ) : (
            portfolios.map(portfolio => (
              <button
                key={portfolio.id}
                onClick={() => selectPortfolio(portfolio)}
                className={`w-full rounded-2xl border px-3 py-3 text-left transition-colors ${
                  selectedPortfolio?.id === portfolio.id
                    ? 'border-blue-200 bg-blue-50 shadow-sm'
                    : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
                }`}
              >
                <div className="flex items-start gap-3">
                  <span className="mt-1 h-3 w-3 shrink-0 rounded-full" style={{ backgroundColor: portfolio.color }} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <p className="truncate font-medium text-slate-900">{portfolio.name}</p>
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-500">
                        {portfolio.board_ids?.length || 0}
                      </span>
                    </div>
                    {portfolio.description && (
                      <p className="mt-1 line-clamp-2 text-xs text-slate-500">{portfolio.description}</p>
                    )}
                  </div>
                </div>
              </button>
            ))
          )}
        </div>

        <Button variant="outline" size="sm" className="mt-4 w-full" onClick={() => setShowCreateModal(true)}>
          + Novo Portfolio
        </Button>
      </aside>

      <main className="flex-1 min-w-0 overflow-y-auto p-4 sm:p-6">
        {!selectedPortfolio ? (
          <div className="flex h-full items-center justify-center">
            <div className="max-w-lg rounded-3xl border border-slate-200 bg-white px-8 py-10 text-center shadow-sm">
              <div className="text-5xl">🗂️</div>
              <h3 className="mt-5 text-2xl font-semibold text-slate-900">Selecione um portfolio</h3>
              <p className="mt-2 text-sm text-slate-500">
                Aqui o objetivo passa a ser leitura executiva: risco, progresso real, carga e boards que pedem decisao.
              </p>
              <Button className="mt-6" onClick={() => setShowCreateModal(true)}>Criar portfolio</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            <header className="flex flex-col gap-4 border-b border-slate-200 pb-5 xl:flex-row xl:items-end xl:justify-between">
              <div>
                <div className="flex items-center gap-3">
                  <span className="h-3 w-3 rounded-full" style={{ backgroundColor: selectedPortfolio.color }} />
                  <h2 className="text-2xl font-bold tracking-tight text-slate-900">{selectedPortfolio.name}</h2>
                </div>
                {selectedPortfolio.description && (
                  <p className="mt-1 max-w-3xl text-sm text-slate-500">{selectedPortfolio.description}</p>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-2.5">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Boards</p>
                  <p className="mt-0.5 text-lg font-semibold text-slate-900">{summary?.board_count || selectedPortfolio.board_ids?.length || 0}</p>
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-2.5">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Prazo critico</p>
                  <p className="mt-0.5 text-lg font-semibold text-slate-900">{(dueBuckets.overdue || 0) + (dueBuckets.today || 0)}</p>
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-2.5">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Carga planejada</p>
                  <p className="mt-0.5 text-lg font-semibold text-slate-900">{formatMinutes(summary?.planned_minutes || 0)}</p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fetchStats(selectedPortfolio.id)}
                >
                  Atualizar visao
                </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                    onClick={() => handleDelete(selectedPortfolio.id)}
                  >
                    Excluir
                  </Button>
              </div>
            </header>

            {statsLoading ? (
              <div className="flex h-40 items-center justify-center"><div className="h-7 w-7 animate-spin rounded-full border-b-2 border-blue-600"></div></div>
            ) : !stats || !summary ? (
              <div className="rounded-3xl border border-slate-200 bg-white p-8 text-sm text-slate-500">
                Nenhum dado consolidado disponivel para este portfolio ainda.
              </div>
            ) : (
              <>
                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-6">
                  <MetricCard
                    label="Escopo total"
                    value={summary.total_items}
                    detail={`${summary.primary_tasks} tarefas pai + ${summary.subtasks} subtarefas`}
                    tone="bg-slate-950 text-white"
                  />
                  <MetricCard
                    label="Progresso real"
                    value={`${summary.progress_percentage}%`}
                    detail={`${summary.completed_items} concluidos · ${summary.in_progress_items} em execucao`}
                    tone="bg-[linear-gradient(135deg,_#0ea5e9,_#2563eb)] text-white"
                  />
                  <MetricCard
                    label="Risco de prazo"
                    value={summary.overdue_items}
                    detail={`${dueBuckets.today || 0} vencem hoje · ${dueBuckets.this_week || 0} ainda esta semana`}
                    tone="bg-[linear-gradient(135deg,_#f59e0b,_#f97316)] text-white"
                  />
                  <MetricCard
                    label="Bloqueios"
                    value={summary.blocked_items}
                    detail={`${summary.boards_at_risk} boards em alerta`}
                    tone="bg-[linear-gradient(135deg,_#fb7185,_#e11d48)] text-white"
                  />
                  <MetricCard
                    label="Esforço"
                    value={formatMinutes(summary.tracked_minutes)}
                    detail={`${formatMinutes(summary.planned_minutes)} planejados`}
                    tone="bg-[linear-gradient(135deg,_#10b981,_#22c55e)] text-slate-950"
                  />
                  <MetricCard
                    label="Time ativo"
                    value={recentActivity.team_members || 0}
                    detail={`${recentActivity.new_items_last_week || 0} itens novos na ultima semana`}
                    tone="bg-[linear-gradient(135deg,_#ede9fe,_#dbeafe)] text-slate-900"
                  />
                </div>

                <div className="grid gap-6 xl:grid-cols-[1.3fr_0.9fr]">
                  <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-blue-600">Radar dos boards</p>
                        <h3 className="mt-1 text-xl font-semibold text-slate-900">Onde o portfolio precisa de ação</h3>
                      </div>
                      <Badge variant="outline">{boardCards.length} boards</Badge>
                    </div>

                    <div className="mt-5 space-y-4">
                      {(riskBoards.length ? riskBoards : boardCards).map(board => (
                        <article key={board.id} className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
                          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                            <div className="min-w-0">
                              <div className="flex items-center gap-3">
                                <h4 className="truncate font-semibold text-slate-900">{board.name}</h4>
                                <span className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold ${getRiskTone(board.risk_score)}`}>
                                  risco {board.risk_score}
                                </span>
                              </div>
                              <p className="mt-1 text-sm text-slate-500">
                                {board.total_items} itens · {board.subtasks} subtarefas · {board.group_count} grupos
                              </p>
                            </div>

                            <div className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-4">
                              <div className="rounded-xl bg-white px-3 py-2 text-center">
                                <p className="text-[11px] uppercase tracking-wide text-slate-400">Progresso</p>
                                <p className="mt-1 font-semibold text-slate-900">{board.progress_percentage}%</p>
                              </div>
                              <div className="rounded-xl bg-white px-3 py-2 text-center">
                                <p className="text-[11px] uppercase tracking-wide text-slate-400">Atrasados</p>
                                <p className="mt-1 font-semibold text-amber-700">{board.overdue_items}</p>
                              </div>
                              <div className="rounded-xl bg-white px-3 py-2 text-center">
                                <p className="text-[11px] uppercase tracking-wide text-slate-400">Bloqueados</p>
                                <p className="mt-1 font-semibold text-rose-700">{board.blocked_items}</p>
                              </div>
                              <div className="rounded-xl bg-white px-3 py-2 text-center">
                                <p className="text-[11px] uppercase tracking-wide text-slate-400">Esforço</p>
                                <p className="mt-1 font-semibold text-emerald-700">{formatMinutes(board.planned_minutes)}</p>
                              </div>
                            </div>
                          </div>

                          <div className="mt-4">
                            <ProgressBar value={board.progress_percentage} color={getCompletionColor(board.progress_percentage)} />
                          </div>

                          <div className="mt-4 flex flex-wrap gap-2 text-xs text-slate-500">
                            <span className="rounded-full bg-white px-2.5 py-1">vencendo hoje: {board.due_today}</span>
                            <span className="rounded-full bg-white px-2.5 py-1">esta semana: {board.due_this_week}</span>
                            <span className="rounded-full bg-white px-2.5 py-1">sem responsavel: {board.unassigned_items}</span>
                            <span className="rounded-full bg-white px-2.5 py-1">rastreado: {formatMinutes(board.tracked_minutes)}</span>
                          </div>
                        </article>
                      ))}
                    </div>
                  </section>

                  <section className="space-y-6">
                    <Card className="rounded-3xl border-slate-200 shadow-sm">
                      <CardContent className="p-5">
                        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-blue-600">Pressao de prazo</p>
                        <h3 className="mt-1 text-xl font-semibold text-slate-900">O que vence agora</h3>
                        <div className="mt-5 grid grid-cols-2 gap-3">
                          {[
                            { label: 'Atrasados', value: dueBuckets.overdue || 0, tone: 'bg-rose-50 text-rose-700' },
                            { label: 'Hoje', value: dueBuckets.today || 0, tone: 'bg-amber-50 text-amber-700' },
                            { label: 'Esta semana', value: dueBuckets.this_week || 0, tone: 'bg-blue-50 text-blue-700' },
                            { label: 'Sem prazo', value: dueBuckets.without_due_date || 0, tone: 'bg-slate-100 text-slate-700' },
                          ].map(bucket => (
                            <div key={bucket.label} className={`rounded-2xl px-4 py-4 ${bucket.tone}`}>
                              <p className="text-xs font-semibold uppercase tracking-[0.14em]">{bucket.label}</p>
                              <p className="mt-2 text-3xl font-bold">{bucket.value}</p>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="rounded-3xl border-slate-200 shadow-sm">
                      <CardContent className="p-5">
                        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-blue-600">Distribuicao</p>
                        <h3 className="mt-1 text-xl font-semibold text-slate-900">Como o portfolio está composto</h3>

                        <div className="mt-5 space-y-4">
                          {statusDistribution.slice(0, 5).map(status => (
                            <DistributionRow
                              key={status.key}
                              label={status.name}
                              count={status.count}
                              percentage={status.percentage}
                              color={getCompletionColor(status.percentage)}
                            />
                          ))}
                          {statusDistribution.length === 0 && (
                            <p className="text-sm text-slate-500">Sem distribuicao de status ainda.</p>
                          )}
                        </div>

                        <div className="mt-6 border-t border-slate-200 pt-5">
                          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Prioridades</p>
                          <div className="mt-3 flex flex-wrap gap-2">
                            {priorityDistribution.slice(0, 6).map(priority => (
                              <span key={priority.key} className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs text-slate-700">
                                {priority.name}: {priority.count}
                              </span>
                            ))}
                            {priorityDistribution.length === 0 && (
                              <span className="text-sm text-slate-500">Sem prioridades mapeadas.</span>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </section>
                </div>

                <div className="grid gap-6 xl:grid-cols-[1fr_1fr]">
                  <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-blue-600">Carga do time</p>
                        <h3 className="mt-1 text-xl font-semibold text-slate-900">Quem concentra mais esforço no portfolio</h3>
                      </div>
                      <Badge variant="outline">{assigneeWorkload.length} pessoas</Badge>
                    </div>

                    <div className="mt-5 space-y-3">
                      {assigneeWorkload.length === 0 && (
                        <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-5 text-sm text-slate-500">
                          Nenhum responsavel consolidado ainda.
                        </div>
                      )}

                      {assigneeWorkload.map(person => (
                        <article key={person.id} className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <div className="flex items-center gap-3">
                                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 text-sm font-bold text-white">
                                  {person.name?.charAt(0)?.toUpperCase() || 'U'}
                                </span>
                                <div>
                                  <p className="truncate font-semibold text-slate-900">{person.name}</p>
                                  <p className="text-xs text-slate-500">
                                    {person.count} itens · {person.subtasks} subtarefas · {person.boards_touched} boards
                                  </p>
                                </div>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="font-semibold text-slate-900">{formatMinutes(person.planned_minutes)}</p>
                              <p className="text-xs text-slate-500">planejado</p>
                            </div>
                          </div>

                          <div className="mt-4 grid grid-cols-3 gap-2 text-center text-xs">
                            <div className="rounded-xl bg-white px-2 py-2">
                              <p className="text-slate-400">Rastreado</p>
                              <p className="mt-1 font-semibold text-emerald-700">{formatMinutes(person.tracked_minutes)}</p>
                            </div>
                            <div className="rounded-xl bg-white px-2 py-2">
                              <p className="text-slate-400">Atrasados</p>
                              <p className="mt-1 font-semibold text-amber-700">{person.overdue_items}</p>
                            </div>
                            <div className="rounded-xl bg-white px-2 py-2">
                              <p className="text-slate-400">Conclusao</p>
                              <p className="mt-1 font-semibold text-slate-900">{person.completion_percentage}%</p>
                            </div>
                          </div>
                        </article>
                      ))}
                    </div>
                  </section>

                  <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-blue-600">Itens criticos</p>
                        <h3 className="mt-1 text-xl font-semibold text-slate-900">Tarefas pai que precisam destravar o portfolio</h3>
                      </div>
                      <Badge variant="outline">{criticalItems.length}</Badge>
                    </div>

                    <div className="mt-5 space-y-3">
                      {criticalItems.length === 0 && (
                        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-5 text-sm text-emerald-700">
                          Nenhum item critico detectado no portfolio neste momento.
                        </div>
                      )}

                      {criticalItems.map(item => (
                        <article key={`${item.board_id}-${item.id}`} className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="truncate font-semibold text-slate-900">{item.name}</p>
                                <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${getRiskTone(item.risk_score)}`}>
                                  risco {item.risk_score}
                                </span>
                              </div>
                              <p className="mt-1 text-xs text-slate-500">
                                {item.board_name} · {item.group_name}
                              </p>
                            </div>
                            <div className="text-right text-xs text-slate-500">
                              <p>{item.progress_percentage}% concluido</p>
                              <p>{formatDateLabel(item.next_due_date || item.latest_due_date)}</p>
                            </div>
                          </div>

                          <div className="mt-4 grid grid-cols-4 gap-2 text-center text-xs">
                            <div className="rounded-xl bg-white px-2 py-2">
                              <p className="text-slate-400">Bloqueios</p>
                              <p className="mt-1 font-semibold text-rose-700">{item.blocked_subtasks}</p>
                            </div>
                            <div className="rounded-xl bg-white px-2 py-2">
                              <p className="text-slate-400">Atrasos</p>
                              <p className="mt-1 font-semibold text-amber-700">{item.overdue_subtasks}</p>
                            </div>
                            <div className="rounded-xl bg-white px-2 py-2">
                              <p className="text-slate-400">Abertas</p>
                              <p className="mt-1 font-semibold text-slate-900">{item.open_subtasks}</p>
                            </div>
                            <div className="rounded-xl bg-white px-2 py-2">
                              <p className="text-slate-400">Esforço</p>
                              <p className="mt-1 font-semibold text-emerald-700">{formatMinutes(item.planned_minutes)}</p>
                            </div>
                          </div>
                        </article>
                      ))}
                    </div>
                  </section>
                </div>

                <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-blue-600">Cobertura do portfolio</p>
                      <h3 className="mt-1 text-xl font-semibold text-slate-900">Boards incluidos na composicao</h3>
                    </div>
                    <p className="text-sm text-slate-500">
                      media de {recentActivity.avg_items_per_board || 0} itens por board
                    </p>
                  </div>

                  <div className="mt-5 flex flex-wrap gap-2">
                    {selectedBoards.length === 0 && (
                      <span className="text-sm text-slate-500">Nenhum board vinculado.</span>
                    )}
                    {selectedBoards.map(board => (
                      <span key={board.id} className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-sm text-slate-700">
                        {board.name}
                      </span>
                    ))}
                  </div>
                </section>
              </>
            )}
          </div>
        )}
      </main>

      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Novo Portfolio</DialogTitle>
          </DialogHeader>

          <div className="mt-2 space-y-4">
            <div>
              <Label>Nome *</Label>
              <Input className="mt-1" placeholder="Ex: Q3 2026" value={form.name} onChange={event => setForm(current => ({ ...current, name: event.target.value }))} />
            </div>

            <div>
              <Label>Descricao</Label>
              <Input className="mt-1" placeholder="Opcional" value={form.description} onChange={event => setForm(current => ({ ...current, description: event.target.value }))} />
            </div>

            <div>
              <Label>Cor</Label>
              <input type="color" className="mt-1 h-9 w-full rounded border" value={form.color} onChange={event => setForm(current => ({ ...current, color: event.target.value }))} />
            </div>

            <div>
              <Label className="mb-2 block">Boards incluidos</Label>
              <div className="max-h-48 space-y-1 overflow-y-auto rounded-md border p-2">
                {(boards || []).map(board => (
                  <label key={board.id} className="flex cursor-pointer items-center space-x-2 rounded p-1 hover:bg-slate-50">
                    <input type="checkbox" checked={form.board_ids.includes(board.id)} onChange={() => toggleBoardInForm(board.id)} className="rounded" />
                    <span className="text-sm text-slate-700">{board.name}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setShowCreateModal(false)}>Cancelar</Button>
              <Button onClick={handleCreate}>Criar Portfolio</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
